import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Fish, Beef, CircleCheck, TriangleAlert } from "lucide-react";
import { SubpageHeader } from "../components/SubpageHeader";
import { Reveal } from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd } from "../components/SEO";
import { track } from "../analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { peixe: Fish, carne: Beef };

export default function ConfirmacaoPage() {
    const { t } = useLanguage();
    useSEO({
        title: t.seo.confirmar.title,
        description: t.seo.confirmar.desc,
        jsonLd: breadcrumbLd([{ name: t.bc.home, path: "/" }, { name: t.bc.confirmar, path: "/confirmar" }]),
    });
    const [nome, setNome] = useState("");
    const [email, setEmail] = useState("");
    const [bilhete, setBilhete] = useState("");
    const [prato, setPrato] = useState(null);
    const [estado, setEstado] = useState("form"); // form | enviando | sucesso
    const [erro, setErro] = useState(null);

    const submeter = async (e) => {
        e.preventDefault();
        setErro(null);
        setEstado("enviando");
        try {
            await axios.post(`${API}/confirmacoes`, { nome, email, bilhete, prato });
            track("Lead", { prato }, { email });
            track("CompleteRegistration", { method: "formulario-confirmacao" }, { email });
            setEstado("sucesso");
        } catch (err) {
            setErro(err.response?.data?.detail || t.confirmacao.erroGenerico);
            setEstado("form");
        }
    };

    const pratoLabel = prato === "peixe" ? t.confirmacao.peixe : t.confirmacao.carne;

    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative overflow-hidden" data-testid="confirmacao-page">
            <div className="absolute inset-0 pointer-events-none">
                <img loading="lazy" decoding="async" src="/images/ambiente.webp" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25 blur-md scale-110" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#070503]/60 via-[#070503]/80 to-[#070503]" />
            </div>
            <SubpageHeader />
            <motion.main
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 mx-auto max-w-[720px] px-5 sm:px-10 pt-44 sm:pt-36 pb-24"
            >
                <Reveal className="text-center">
                    <p className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]">{t.confirmacao.label}</p>
                    <div className="flex justify-center mt-4">
                        <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-16 opacity-90" />
                    </div>
                    <h1 className="font-display font-medium gold-text text-3xl sm:text-5xl leading-[1.05] mt-6" data-testid="confirmacao-title">
                        {t.confirmacao.titulo}
                    </h1>
                    <p className="font-display font-light text-lg text-[#efe6d6]/70 leading-relaxed max-w-xl mx-auto mt-6">
                        {t.confirmacao.introA}
                        <span className="text-[#ecd49b]">{t.confirmacao.prazoData}</span>.
                    </p>
                </Reveal>

                {estado === "sucesso" ? (
                    <Reveal className="mt-12">
                        <div className="border border-[#c9a24b]/40 bg-[#0a0705]/80 p-10 text-center" data-testid="confirmacao-sucesso">
                            <CircleCheck size={44} strokeWidth={1.2} className="text-[#c9a24b] mx-auto" />
                            <h2 className="font-display font-light text-2xl sm:text-3xl text-[#efe6d6] mt-6">{t.confirmacao.sucessoTitulo}</h2>
                            <p className="font-light text-[#efe6d6]/70 mt-4 leading-relaxed">
                                {t.confirmacao.sucessoTexto.replace("{nome}", nome.split(" ")[0]).replace("{prato}", pratoLabel)}
                            </p>
                        </div>
                    </Reveal>
                ) : (
                    <Reveal delay={0.15} className="mt-12">
                        <form onSubmit={submeter} className="border border-[#c9a24b]/30 bg-[#0a0705]/80 p-8 sm:p-10 space-y-7" data-testid="confirmacao-form">
                            <div>
                                <label htmlFor="nome" className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] block mb-2.5">
                                    {t.confirmacao.nome}
                                </label>
                                <input
                                    id="nome"
                                    type="text"
                                    required
                                    minLength={2}
                                    value={nome}
                                    onChange={(e) => setNome(e.target.value)}
                                    data-testid="confirmacao-nome"
                                    className="w-full bg-[#070503] border border-[#c9a24b]/30 px-4 py-3 font-light text-[#efe6d6] placeholder:text-[#efe6d6]/30 focus:border-[#c9a24b] focus:outline-none transition-colors duration-300"
                                    placeholder={t.confirmacao.nomePh}
                                />
                            </div>
                            <div>
                                <label htmlFor="email" className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] block mb-2.5">
                                    {t.confirmacao.email}
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="confirmacao-email"
                                    className="w-full bg-[#070503] border border-[#c9a24b]/30 px-4 py-3 font-light text-[#efe6d6] placeholder:text-[#efe6d6]/30 focus:border-[#c9a24b] focus:outline-none transition-colors duration-300"
                                    placeholder={t.confirmacao.emailPh}
                                />
                            </div>
                            <div>
                                <label htmlFor="bilhete" className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] block mb-2.5">
                                    {t.confirmacao.bilhete}
                                </label>
                                <input
                                    id="bilhete"
                                    type="text"
                                    required
                                    value={bilhete}
                                    onChange={(e) => setBilhete(e.target.value)}
                                    data-testid="confirmacao-bilhete"
                                    className="w-full bg-[#070503] border border-[#c9a24b]/30 px-4 py-3 font-light text-[#efe6d6] placeholder:text-[#efe6d6]/30 focus:border-[#c9a24b] focus:outline-none transition-colors duration-300"
                                    placeholder={t.confirmacao.bilhetePh}
                                />
                            </div>
                            <div>
                                <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] mb-3">{t.confirmacao.escolhePrato}</p>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {t.confirmacao.opcoes.map((o) => {
                                        const Icon = ICONS[o.id];
                                        return (
                                            <button
                                                key={o.id}
                                                type="button"
                                                onClick={() => setPrato(o.id)}
                                                data-testid={`prato-${o.id}`}
                                                className={`text-left border p-5 transition-colors duration-300 ${
                                                    prato === o.id
                                                        ? "border-[#c9a24b] bg-[#c9a24b]/10"
                                                        : "border-[#c9a24b]/25 hover:border-[#c9a24b]/60"
                                                }`}
                                            >
                                                <Icon size={24} strokeWidth={1.3} className="text-[#c9a24b]" />
                                                <p className="font-caps text-xs tracking-[0.15em] uppercase text-[#efe6d6] mt-3">{o.title}</p>
                                                <p className="font-light text-sm text-[#efe6d6]/60 mt-1.5 leading-relaxed">{o.desc}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {erro && (
                                <p className="flex items-center gap-2 text-sm font-light text-[#e0a0a0]" data-testid="confirmacao-erro">
                                    <TriangleAlert size={16} /> {erro}
                                </p>
                            )}

                            <button
                                type="submit"
                                disabled={estado === "enviando" || !prato}
                                data-testid="confirmacao-submit"
                                className="w-full font-caps text-sm tracking-[0.25em] uppercase bg-[#5e1224] text-[#efe6d6] border border-[#c9a24b]/50 px-10 py-4 hover:bg-[#7a1a30] hover:border-[#c9a24b] transition-colors duration-400 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {estado === "enviando" ? t.confirmacao.aRegistar : t.confirmacao.submit}
                            </button>
                        </form>
                    </Reveal>
                )}
            </motion.main>
        </div>
    );
}
